// ============================================================
// SpringBootRestControllers.jsx
//
// A branded, responsive course content page for the
// "REST Controllers in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: RESTful mapping logic, JSON serialization,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import HttpIcon               from "@mui/icons-material/Http";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import HubIcon                from "@mui/icons-material/Hub";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootRestControllers() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 05"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        REST <span>Controllers</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Expose your Java application to the world. Using Spring Web, you can build powerful RESTful APIs that automatically serialize Java objects into JSON responses, handling all standard HTTP methods with minimal configuration.
                    </Typography>
                </Box>

                {/* 1. REST VS VIEW CONTROLLERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SwapHorizIcon />} label="1. @RestController vs @Controller" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A standard <code style={{color: BRAND.white}}>@Controller</code> usually returns an HTML view template. By using <code style={{color: BRAND.white}}>@RestController</code>, you tell Spring to write the return data directly into the HTTP response body—perfect for APIs.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>@Controller</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>Used for Server-Side Rendering (Thymeleaf, JSP). Returns the name of an HTML template.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.gold}`, borderRadius: 2 }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 700, mb: 1 }}>@RestController</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 12 }}>The modern choice for APIs. Equivalent to @Controller + @ResponseBody. Returns JSON/XML.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. MAPPING METHODS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HttpIcon />} label="2. Mapping HTTP Verbs" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Map specific HTTP request types to your methods using semantic annotations. This creates a clean, predictable API surface.
                    </Typography>
                    <CodeBlock
                        title="Semantic Mapping"
                        code={`@GetMapping("/users")     // Retrieve resources\n@PostMapping("/users")    // Create new resources\n@PutMapping("/users/{id}") // Update existing resources\n@DeleteMapping("/users/{id}") // Remove resources`}
                    />
                </Box>

                {/* 3. CRUD IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="3. Real-World Implementation" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Combine <code style={{color: BRAND.white}}>@RequestMapping</code> at the class level with dependency injection to build modular endpoint handlers.
                    </Typography>
                    <CodeBlock
                        title="UserController.java"
                        code={`@RestController\n@RequestMapping("/api/users")\npublic class UserController {\n\n    private final UserService service;\n\n    public UserController(UserService service) {\n        this.service = service;\n    }\n\n    @PostMapping\n    public User create(@RequestBody User u) {\n        return service.save(u);\n    }\n}`}
                    />
                </Box>

                {/* 4. SERIALIZATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HubIcon />} label="4. Auto-JSON Serialization" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Spring Boot bundles the <strong>Jackson</strong> library. It automatically converts Java Maps, Lists, and POJOs into valid JSON strings without extra code.
                    </Typography>
                    <CodeBlock
                        title="Status Endpoint"
                        code={`@GetMapping("/health")\npublic Map<String, String> getHealth() {\n    // Result: {"status": "UP", "uptime": "99.9%"}\n    return Map.of("status", "UP", "uptime", "99.9%");\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="REST Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Use plural nouns for resource paths (/users, not /getUser)",
                            "Return appropriate HTTP Status Codes (201 Created, 404 Not Found)",
                            "Keep controllers thin; delegate logic to Service beans",
                            "Use DTOs (Data Transfer Objects) instead of exposing Entities",
                            "Leverage @PathVariable for IDs and @RequestParam for filtering",
                            "Enable CORS if the API will be consumed by a browser frontend"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Use the <code>ResponseEntity&lt;T&gt;</code> wrapper if you need granular control over the HTTP headers or response status (e.g., returning <code>201 Created</code> along with the object).
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
